import { Globe, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/common/empty-state";
import { SkeletonItem } from "@/components/common/skeleton-item";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreateWebhookButton } from "@/features/webhooks/components/CreateWebhookButton";
import { DeleteWebhookDialog } from "@/features/webhooks/components/DeleteWebhookDialog";
import { useOrgWebhooks } from "@/features/webhooks/hooks/useWebhooks";

export default function WebhookList() {
  const { data: webhooks, isLoading, error, isError } = useOrgWebhooks();
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error("Failed to load webhooks", {
        description:
          error instanceof Error
            ? error.message
            : "Please try again or contact support",
      });
    }
  }, [isLoading, isError, error]);

  return (
    <div className="dashboard-page">
      <div className="page-heading">
        <div>
          <h1 className="page-title">Webhooks</h1>
          <p className="mt-2.5 text-sm text-muted-foreground">
            Configure endpoints to receive event notifications.
          </p>
        </div>
        <div className="shrink-0">
          <CreateWebhookButton />
        </div>
      </div>

      {/* Webhooks List */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle>Your Webhooks</CardTitle>
        </CardHeader>
        <CardContent className="px-6">
          {isLoading ? (
            <SkeletonItem />
          ) : webhooks?.length === 0 ? (
            <EmptyState
              icon={Globe}
              title="No webhooks"
              description="Get started by creating a new webhook endpoint."
            />
          ) : (
            <div className="divide-y divide-border">
              {webhooks?.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex min-h-22 items-center justify-between gap-4 py-4 text-sm"
                >
                  <div className="min-w-0 space-y-1 break-words">
                    <p className="text-[15px] font-medium">{webhook.name}</p>
                    <p className="mt-1 text-[13px] truncate text-muted-foreground">
                      {webhook.url}
                    </p>
                    <p className="text-[13px] truncate text-muted-foreground">
                      {webhook.secret_preview}
                    </p>
                    <p className="flex flex-wrap gap-2 mt-2">
                      {webhook.event_types.map((event) => (
                        <Badge key={event}>{event}</Badge>
                      ))}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Delete Webhook"
                    className="size-11 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setDeleteWebhookId(webhook.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <DeleteWebhookDialog
        webhookId={deleteWebhookId}
        onClose={() => setDeleteWebhookId(null)}
      />
    </div>
  );
}
